/**
 * Ввод денежных сумм. На сервер уходят целые минорные единицы, человек
 * набирает привычное «1 234,56» (docs/04-finance.md).
 */

/** Убирает всё, что человек мог поставить как разделитель разрядов. */
function clean(input: string): string {
  return input
    .replace(/[   \s]/g, '')
    .replace(/−/g, '-')
    .replace(',', '.');
}

/** Строка ввода в минорные единицы. Пусто и мусор дают null. */
export function parseMinor(input: string): number | null {
  const text = clean(input.trim());
  if (text === '' || text === '-') return null;
  if (!/^-?\d*\.?\d*$/.test(text)) return null;
  const value = Number(text);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

/** Минорные единицы в строку для поля ввода: без разделителя разрядов. */
export function minorToInput(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return '';
  return (minor / 100).toFixed(2).replace('.', ',');
}

/**
 * Курс: рублей за единицу валюты сервиса. Хранится умноженным на сто,
 * чтобы не тащить дробное число через границу приложения.
 */
export function parseRate(input: string): number | null {
  const minor = parseMinor(input);
  if (minor === null || minor <= 0) return null;
  return minor;
}

export function rateToInput(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '';
  return rate.toFixed(2).replace('.', ',');
}

/** Процент комиссии хранится умноженным на сто: 250 это 2,5%. */
export function parsePercent(input: string): number | null {
  return parseMinor(input);
}

export function percentToInput(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return (value / 100).toFixed(2).replace(/[.,]?0+$/, '').replace('.', ',');
}

/**
 * Подсказка суммы списания при пополнении кошелька. Та же формула, что
 * на сервере в calculations.ts: центы, умноженные на курс, дают копейки.
 */
export function suggestPaidAmount(
  creditedMinor: number,
  rateTimesHundred: number,
  feeKind: 'fixed' | 'percent' | 'none',
  feeValue: number,
): number {
  const base = (creditedMinor * rateTimesHundred) / 100;
  if (feeKind === 'fixed') return Math.round(base + feeValue);
  if (feeKind === 'percent') return Math.round(base * (1 + feeValue / 10_000));
  return Math.round(base);
}

/** Дата в формате поля ввода. Отображение московское, хранение в UTC. */
export function todayInputValue(now: Date = new Date()): string {
  const moscow = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  return moscow.toISOString().slice(0, 10);
}

/** Значение поля даты в момент времени для сервера: полдень UTC выбранного дня. */
export function dateInputToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  return `${value}T12:00:00.000Z`;
}
